import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Clock } from 'lucide-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type SetAutoShutdownDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (timestamp?: number) => void
  currentAutoShutdown?: string
}

export const SetAutoShutdownDialog = ({
  isOpen,
  onClose,
  onSubmit,
  currentAutoShutdown,
}: SetAutoShutdownDialogProps) => {
  const getInitialDatetime = () => {
    if (currentAutoShutdown) {
      // Convert UTC time to local time for display in datetime-local input
      // The server returns time in UTC, we need to display it in local time
      const localDate = dayjs(currentAutoShutdown).local()
      return localDate.format('YYYY-MM-DDTHH:mm')
    }
    // Default to 2 hours from now
    const now = dayjs().add(2, 'hour')
    return now.format('YYYY-MM-DDTHH:mm')
  }

  const [datetime, setDatetime] = useState(getInitialDatetime())

  const onScheduleClick = () => {
    // Parse the datetime-local input value as local time and convert to UTC
    // datetime-local format: "2025-10-06T12:40" (no timezone info)
    // dayjs will treat this as local time by default
    const localDate = dayjs(datetime)
    // Convert to UTC and get Unix timestamp (in seconds)
    const timestamp = localDate.utc().unix()
    onSubmit(timestamp)
    onClose()
  }

  const onClearClick = () => {
    onSubmit(undefined)
    onClose()
  }

  const minDatetime = dayjs().format('YYYY-MM-DDTHH:mm')

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={20} />
            Schedule Auto-Shutdown
          </DialogTitle>
          <DialogDescription>
            Server will automatically shut down at the scheduled time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            type="datetime-local"
            value={datetime}
            onChange={(e) => setDatetime(e.target.value)}
            min={minDatetime}
          />
          <div className="flex justify-between gap-3">
            {currentAutoShutdown && (
              <Button variant="outline" onClick={onClearClick}>
                Clear Schedule
              </Button>
            )}
            <div className="flex gap-3 ml-auto">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={onScheduleClick}>Schedule</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
